// tangilex placeholder — liquid glass

import * as React from 'react';
import {
  useTweaks,
  TweaksPanel,
  TweakSection,
  TweakSlider,
  TweakToggle,
  TweakRadio,
  TweakSelect,
  TweakText,
  TweakColor,
} from './tweaks-panel.jsx';

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "hue": 155,
  "chroma": 0.13,
  "blur": 68,
  "tint": 50,
  "borderGlow": 100,
  "innerHighlight": 100,
  "noise": 18,
  "shineSpeed": 3,
  "shineWidth": 73,
  "shineEnabled": true,
  "orbCount": 4,
  "orbSize": 540,
  "orbSpeed": 22,
  "orbOpacity": 65,
  "background": "grid",
  "dark": false,
  "font": "geist",
  "logoWeight": 600,
  "logoTracking": -6,
  "logoSize": 22,
  "logoTop": 0,
  "logoRight": 0,
  "showCategories": false,
  "showSubscribe": true,
  "layout": "stack",
  "headline": "the marketplace for tangible things",
  "sub": "Buy fractional shares in the assets that move the real world — wind farms, apartments, art, vehicles, and more. Trade tangibility.",
  "cornerRadius": 40,
  "cardWidth": 620,
  "padding": 54,
  "showOrbs": true,
  "vignette": 35,
  "letterboxing": false
}/*EDITMODE-END*/;

const FONT_STACKS = {
  geist: `'Geist', system-ui, sans-serif`,
  grotesk: `'Familjen Grotesk', system-ui, sans-serif`,
  serif: `'Instrument Serif', Georgia, serif`,
  mono: `'JetBrains Mono', ui-monospace, monospace`,
};

const CATEGORIES = [
  { label: "wind farms",  hint: "renewable yield" },
  { label: "apartments",  hint: "residential" },
  { label: "fine art",    hint: "collectibles" },
  { label: "vehicles",    hint: "depreciating" },
  { label: "warehouses",  hint: "industrial" },
  { label: "vineyards",   hint: "agricultural" },
];

function Logo({ weight, tracking, font, size }) {
  return (
    <div className="logo" style={{ fontFamily: FONT_STACKS[font], fontWeight: weight,
      letterSpacing: `${tracking / 100}em`, fontSize: size }}>
      <span className="logo-word">tangilex</span>
      <span className="logo-shine" />
    </div>
  );
}

function Background({ t, accentSoft, accentBright, accentDeep }) {
  const orbs = React.useMemo(() => {
    const N = 16;
    const seed = (i) => {
      const x = Math.sin(i * 9301 + 49297) * 233280;
      return x - Math.floor(x);
    };
    return Array.from({ length: N }, (_, i) => ({
      x: seed(i) * 100,
      y: seed(i + 100) * 100,
      delay: seed(i + 200) * 20,
      duration: 18 + seed(i + 300) * 18,
      scale: 0.6 + seed(i + 400) * 0.9,
      color: i % 3 === 0 ? accentBright : i % 3 === 1 ? accentSoft : accentDeep,
    }));
  }, [accentSoft, accentBright, accentDeep]);

  return (
    <div className="bg" data-bg={t.background}>
      {(t.background === "mesh" || t.background === "aurora") && (
        <>
          <div className="bg-aurora a1" style={{ background: `radial-gradient(circle at 20% 30%, ${accentBright} 0%, transparent 45%)` }} />
          <div className="bg-aurora a2" style={{ background: `radial-gradient(circle at 80% 20%, ${accentSoft} 0%, transparent 50%)` }} />
          <div className="bg-aurora a3" style={{ background: `radial-gradient(circle at 60% 85%, ${accentDeep} 0%, transparent 45%)` }} />
          <div className="bg-aurora a4" style={{ background: `radial-gradient(circle at 10% 90%, ${accentBright} 0%, transparent 40%)` }} />
        </>
      )}
      {t.showOrbs && orbs.slice(0, t.orbCount).map((o, i) => (
        <div key={i} className="bg-orb"
          style={{
            left: `${o.x}%`, top: `${o.y}%`,
            width: t.orbSize, height: t.orbSize,
            background: `radial-gradient(circle, ${o.color} 0%, transparent 70%)`,
            opacity: t.orbOpacity / 100,
            animationDuration: `${o.duration * (30 / t.orbSpeed)}s`,
            animationDelay: `-${o.delay}s`,
            transform: `translate(-50%, -50%) scale(${o.scale})`,
          }} />
      ))}
      {t.background === "grid" && <div className="bg-grid" />}
      <div className="bg-vignette" style={{ opacity: t.vignette / 100 }} />
      <div className="bg-grain" style={{ opacity: t.noise / 100 }} />
    </div>
  );
}

function GlassCard({ t, children }) {
  const cardRef = React.useRef(null);
  React.useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      el.style.setProperty('--mx', `${x}%`);
      el.style.setProperty('--my', `${y}%`);
    };
    const onLeave = () => {
      el.style.setProperty('--mx', `50%`);
      el.style.setProperty('--my', `0%`);
    };
    window.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <div ref={cardRef} className="glass" style={{
      width: t.cardWidth, padding: t.padding, borderRadius: t.cornerRadius,
      backdropFilter: `blur(${t.blur}px) saturate(180%)`,
      WebkitBackdropFilter: `blur(${t.blur}px) saturate(180%)`,
      background: `rgba(255, 255, 255, ${t.tint / 100})`,
      borderColor: `rgba(255, 255, 255, ${t.borderGlow / 100})`,
    }}>
      <div className="glass-highlight" style={{
        opacity: t.innerHighlight / 100,
        background: `linear-gradient(180deg, rgba(255,255,255,0.9) 0%, transparent 35%)`,
        borderRadius: t.cornerRadius,
      }} />
      <div className="glass-specular" style={{
        background: `radial-gradient(circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.35) 0%, transparent 40%)`,
        borderRadius: t.cornerRadius,
      }} />
      {t.shineEnabled && (
        <div className="glass-shine" style={{
          animationDuration: `${t.shineSpeed}s`,
          borderRadius: t.cornerRadius,
        }}>
          <div className="glass-shine-bar" style={{
            width: `${t.shineWidth}%`,
            background: `linear-gradient(105deg, transparent 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 70%, transparent 100%)`,
          }} />
        </div>
      )}
      <div className="glass-edge" style={{ borderRadius: t.cornerRadius }} />
      <div className="glass-content">{children}</div>
    </div>
  );
}

function SubscribeRow({ accent, font }) {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setSent(true);
    setTimeout(() => { setSent(false); setEmail(""); }, 2400);
  };
  return (
    <form className="sub" onSubmit={submit}>
      <div className="sub-glass">
        <input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ fontFamily: FONT_STACKS[font] }}
        />
        <button type="submit" style={{ background: accent, fontFamily: FONT_STACKS[font] }}>
          {sent ? "✓  on the list" : "notify me"}
          <span className="btn-shine" />
        </button>
      </div>
    </form>
  );
}

function CategoryChips({ items, font }) {
  return (
    <div className="cats">
      {items.map((c, i) => (
        <div key={c.label} className="cat" style={{ animationDelay: `${i * 80}ms` }}>
          <span className="cat-dot" />
          <span className="cat-lbl" style={{ fontFamily: FONT_STACKS[font] }}>{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function StatusPill({ font, accent }) {
  return (
    <div className="status" style={{ fontFamily: FONT_STACKS[font] }}>
      <span className="status-dot" style={{ background: accent }}>
        <span className="status-pulse" style={{ background: accent }} />
      </span>
      <span>building · launching Q3 2026</span>
    </div>
  );
}

export default function App() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  const accent       = `oklch(0.62 ${t.chroma} ${t.hue})`;
  const accentBright = `oklch(0.78 ${t.chroma * 0.85} ${t.hue})`;
  const accentSoft   = `oklch(0.88 ${t.chroma * 0.5} ${t.hue + 8})`;
  const accentDeep   = `oklch(0.42 ${t.chroma} ${t.hue - 10})`;
  const fg           = t.dark ? `oklch(0.97 0.005 ${t.hue})` : `oklch(0.18 0.01 ${t.hue})`;
  const fgSoft       = t.dark ? `oklch(0.78 0.01 ${t.hue})`  : `oklch(0.38 0.01 ${t.hue})`;

  const bgBase = t.dark
    ? `oklch(0.16 0.015 ${t.hue})`
    : `oklch(0.97 0.008 ${t.hue})`;

  React.useEffect(() => {
    document.body.style.background = bgBase;
    document.documentElement.style.setProperty('--fg', fg);
    document.documentElement.style.setProperty('--fg-soft', fgSoft);
    document.documentElement.style.setProperty('--accent', accent);
    document.documentElement.style.setProperty('--accent-bright', accentBright);
  }, [bgBase, fg, fgSoft, accent, accentBright]);

  return (
    <div className="stage" style={{ color: fg, fontFamily: FONT_STACKS[t.font] }}>
      <Background t={t} accentSoft={accentSoft} accentBright={accentBright} accentDeep={accentDeep} />

      <main className="main" data-layout={t.layout}>
        <GlassCard t={t}>
          <div className="card-logo" style={{
            top: t.logoTop,
            right: t.logoRight,
          }}>
            <Logo weight={t.logoWeight} tracking={t.logoTracking} font={t.font} size={t.logoSize} />
          </div>
          <div className="hero">
            <StatusPill font={t.font} accent={accent} />

            <h1 className="headline" style={{
              fontFamily: FONT_STACKS[t.font === "serif" ? "serif" : t.font],
              letterSpacing: t.font === "serif" ? "-0.01em" : "-0.025em",
            }}>
              {t.headline}
            </h1>

            <p className="sub-copy" style={{ color: fgSoft }}>
              {t.sub}
            </p>

            {t.showCategories && (
              <>
                <div className="rule" />
                <div className="cats-label" style={{ color: fgSoft }}>
                  <span>opening with</span>
                  <span className="rule-dots">· · · · · · · · · · · · · · · · ·</span>
                </div>
                <CategoryChips items={CATEGORIES} font={t.font} />
              </>
            )}

            {t.showSubscribe && (
              <>
                <div className="rule" />
                <SubscribeRow accent={accent} font={t.font} />
                <div className="sub-note" style={{ color: fgSoft }}>
                  Tangilex Limited · regulated tangible-asset exchange · tangilex.io
                </div>
              </>
            )}
          </div>
        </GlassCard>
      </main>

      <footer className="bot" style={{ color: fgSoft, fontFamily: FONT_STACKS[t.font] }}>
        <span>© {new Date().getFullYear()} tangilex limited</span>
        <span className="bot-sep">·</span>
        <span>built for things you can touch</span>
      </footer>

      {import.meta.env.DEV && (
        <TweaksPanel title="tangilex tweaks">
          <TweakSection label="Glass" />
          <TweakSlider label="Blur"          value={t.blur}          min={0}  max={80}  unit="px"
                       onChange={(v) => setTweak('blur', v)} />
          <TweakSlider label="Tint opacity"  value={t.tint}          min={0}  max={60}  unit="%"
                       onChange={(v) => setTweak('tint', v)} />
          <TweakSlider label="Border glow"   value={t.borderGlow}    min={0}  max={100} unit="%"
                       onChange={(v) => setTweak('borderGlow', v)} />
          <TweakSlider label="Inner highlight" value={t.innerHighlight} min={0} max={100} unit="%"
                       onChange={(v) => setTweak('innerHighlight', v)} />
          <TweakSlider label="Corner radius" value={t.cornerRadius} min={0} max={64} unit="px"
                       onChange={(v) => setTweak('cornerRadius', v)} />
          <TweakSlider label="Card width"    value={t.cardWidth}    min={420} max={900} unit="px" step={10}
                       onChange={(v) => setTweak('cardWidth', v)} />
          <TweakSlider label="Padding"       value={t.padding}      min={24} max={96} unit="px" step={2}
                       onChange={(v) => setTweak('padding', v)} />

          <TweakSection label="Shine" />
          <TweakToggle label="Sweeping shine" value={t.shineEnabled}
                       onChange={(v) => setTweak('shineEnabled', v)} />
          <TweakSlider label="Sweep speed"    value={t.shineSpeed} min={2} max={20} unit="s"
                       onChange={(v) => setTweak('shineSpeed', v)} />
          <TweakSlider label="Sweep width"    value={t.shineWidth} min={10} max={80} unit="%"
                       onChange={(v) => setTweak('shineWidth', v)} />

          <TweakSection label="Color" />
          <TweakColor  label="Green preset"
                       value={`oklch(0.62 ${t.chroma} ${t.hue})`}
                       options={[
                         [148, 0.16],
                         [155, 0.13],
                         [142, 0.20],
                         [165, 0.10],
                         [125, 0.18],
                       ].map(([h, c]) => `oklch(0.62 ${c} ${h})`)}
                       onChange={(v) => {
                         const m = /oklch\(0\.62\s+([\d.]+)\s+(\d+)\)/.exec(v);
                         if (m) setTweak({ chroma: Number(m[1]), hue: Number(m[2]) });
                       }} />
          <TweakSlider label="Hue"     value={t.hue}    min={90}  max={200} unit="°"
                       onChange={(v) => setTweak('hue', v)} />
          <TweakSlider label="Chroma"  value={Math.round(t.chroma * 100) / 100} min={0.04} max={0.28} step={0.01}
                       onChange={(v) => setTweak('chroma', v)} />
          <TweakToggle label="Dark mode" value={t.dark}
                       onChange={(v) => setTweak('dark', v)} />

          <TweakSection label="Background" />
          <TweakRadio  label="Style" value={t.background}
                       options={['mesh', 'aurora', 'grid']}
                       onChange={(v) => setTweak('background', v)} />
          <TweakToggle label="Show orbs" value={t.showOrbs}
                       onChange={(v) => setTweak('showOrbs', v)} />
          <TweakSlider label="Orb count"   value={t.orbCount}  min={0}  max={12}
                       onChange={(v) => setTweak('orbCount', v)} />
          <TweakSlider label="Orb size"    value={t.orbSize}   min={200} max={900} unit="px" step={10}
                       onChange={(v) => setTweak('orbSize', v)} />
          <TweakSlider label="Orb speed"   value={t.orbSpeed}  min={5}  max={60}
                       onChange={(v) => setTweak('orbSpeed', v)} />
          <TweakSlider label="Orb opacity" value={t.orbOpacity} min={10} max={100} unit="%"
                       onChange={(v) => setTweak('orbOpacity', v)} />
          <TweakSlider label="Grain"       value={t.noise}     min={0} max={60} unit="%"
                       onChange={(v) => setTweak('noise', v)} />
          <TweakSlider label="Vignette"    value={t.vignette}  min={0} max={80} unit="%"
                       onChange={(v) => setTweak('vignette', v)} />

          <TweakSection label="Type" />
          <TweakSelect label="Font family" value={t.font}
                       options={[
                         { value: 'geist',   label: 'Geist (sans)' },
                         { value: 'grotesk', label: 'Familjen Grotesk' },
                         { value: 'serif',   label: 'Instrument Serif' },
                         { value: 'mono',    label: 'JetBrains Mono' },
                       ]}
                       onChange={(v) => setTweak('font', v)} />

          <TweakSection label="Logo" />
          <TweakSlider label="Size"          value={t.logoSize}     min={12} max={36} unit="px"
                       onChange={(v) => setTweak('logoSize', v)} />
          <TweakSlider label="Top offset"    value={t.logoTop}      min={0}  max={80} unit="px"
                       onChange={(v) => setTweak('logoTop', v)} />
          <TweakSlider label="Right offset"  value={t.logoRight}    min={0}  max={80} unit="px"
                       onChange={(v) => setTweak('logoRight', v)} />
          <TweakSlider label="Weight"        value={t.logoWeight}   min={300} max={700} step={100}
                       onChange={(v) => setTweak('logoWeight', v)} />
          <TweakSlider label="Tracking"      value={t.logoTracking} min={-8} max={4} unit="·.01em"
                       onChange={(v) => setTweak('logoTracking', v)} />

          <TweakSection label="Content" />
          <TweakToggle label="Asset chips"  value={t.showCategories}
                       onChange={(v) => setTweak('showCategories', v)} />
          <TweakToggle label="Email signup" value={t.showSubscribe}
                       onChange={(v) => setTweak('showSubscribe', v)} />
          <TweakText   label="Headline"     value={t.headline}
                       onChange={(v) => setTweak('headline', v)} />
          <TweakText   label="Sub copy"     value={t.sub}
                       onChange={(v) => setTweak('sub', v)} />
        </TweaksPanel>
      )}
    </div>
  );
}
